import { ExclamationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef } from "react";
import { lockScroll, unlockScroll } from "@/utils/scrollLock";

export interface ModalErrorProps {
  isvisible: boolean;
  onClose: () => void;
  error?: string;
  title?: string;
  message?: string | React.ReactNode;
  // Extra detail rendered in its own scrollable block below `message`,
  // so a long list (e.g. customers not found) can scroll without
  // pushing the modal's message/buttons around.
  details?: string | React.ReactNode;
  setIsSelectDisabled?: (disabled: boolean) => void;
  handleKeyPress?: () => void;
  handleDateRef?: () => void;
  setAccept?: (accept: boolean) => void;
  errorList?: [];
  setColorButton?: (color: boolean | string) => void;
  indexSelect?: number | null;
  selectRefs?: React.MutableRefObject<any[] | null>;
  focusSelectInRow?: (index: number | null, refs: any) => void;
  isEditTable?: boolean;
  sizeSmall?: boolean;
  sizeAuto?: boolean;
  buttonLogout?: boolean;
  autoCloseMs?: number;
  // Lets a caller stack this above another already-open modal (e.g. a
  // create/upload modal that stays open behind the error) - defaults to the
  // original z-index so every other usage is unaffected.
  zIndexClassName?: string;
}

const ModalError = ({
  isvisible,
  onClose,
  error = "",
  title = "",
  message = "",
  details = "",
  setIsSelectDisabled = undefined,
  handleKeyPress = () => {},
  handleDateRef = () => {},
  setAccept = () => {},
  errorList = [],
  setColorButton = () => {},
  indexSelect = null,
  selectRefs = undefined,
  focusSelectInRow = () => {},
  isEditTable = false,
  sizeSmall = false,
  sizeAuto = false,
  buttonLogout = false,
  autoCloseMs = undefined,
  zIndexClassName = "z-[500]",
}: ModalErrorProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isvisible) {
      lockScroll();
      if (modalRef.current) modalRef.current.focus();
      if (autoCloseMs) {
        const fallbackTimer = setTimeout(onClose, autoCloseMs);
        return () => {
          unlockScroll();
          clearTimeout(fallbackTimer);
        };
      }
      return () => unlockScroll();
    }
  }, [isvisible]);

  if (!isvisible) {
    return null;
  }

  if (isvisible && setIsSelectDisabled) {
    setIsSelectDisabled(false);
  }

  const handleKeyCloseModal = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === "Escape") {
      onClose();
      handleKeyPress();
      setAccept(true);
      if (isEditTable && selectRefs?.current) {
        if (indexSelect !== null && selectRefs.current[indexSelect]) {
          selectRefs.current[indexSelect].focus();
        }
      } else {
        focusSelectInRow(indexSelect, selectRefs);
      }
    }
  };

  const handleAccept = () => {
    onClose();
    handleKeyPress();
    setAccept(true);
  };

  const handleClose = () => {
    if (errorList) {
      handleDateRef();
      onClose();
      setAccept(false);
    } else {
      onClose();
      setColorButton(false);
      if (isEditTable && selectRefs?.current) {
        if (indexSelect !== null && selectRefs.current[indexSelect]) {
          selectRefs.current[indexSelect].focus();
        }
      } else {
        focusSelectInRow(indexSelect, selectRefs);
      }
    }
  };
  
  return (
    <div
      ref={modalRef}
      tabIndex={0}
      onKeyDown={handleKeyCloseModal}
      className={`fixed inset-0 flex flex-col items-center justify-center bg-black bg-opacity-25 backdrop-blur-sm ${zIndexClassName}`}
    >
      <div
        className={`bg-white p-8 rounded-[28px] ${
          sizeSmall ? "w-[280px] " : sizeAuto ? "w-auto mx-2" : "w-[600px] max-w-[90vw] mx-4"
        } max-h-[85vh] overflow-hidden flex flex-col items-center `}
      >
        <button
          type='button'
          className='place-self-end rounded-full p-1 text-dark-blue transition-colors hover:bg-gray-input'
          onClick={() => onClose()}
        >
          <XMarkIcon className='w-6 h-6 text-gray-500' />
        </button>
        <ExclamationCircleIcon className='w-12 h-12 mb-2 text-danger' />
        <h1 className='mb-2 text-2xl font-medium text-center text-dark-blue'>
          {title}
        </h1>
        <p className='text-lg text-center text-dark-blue'>{error}</p>
        {details ? (
          <>
            <div className='w-full px-1 text-lg text-center break-words text-dark-blue'>
              {typeof message === "string"
                ? message.split(/\n|<br\s*\/?>/).map((line, idx, arr) => (
                    <div
                      key={idx}
                      style={{
                        marginBottom: idx !== arr.length - 1 ? "1px" : "0",
                      }}
                    >
                      {line}
                    </div>
                  ))
                : message}
            </div>
            <div className='w-full max-h-[35vh] overflow-y-auto px-1 mt-2 text-base text-center break-words text-dark-blue [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-thumb]:bg-[#d7dbe3]'>
              {typeof details === "string"
                ? details.split(/\n|<br\s*\/?>/).map((line, idx, arr) => (
                    <div
                      key={idx}
                      style={{
                        marginBottom: idx !== arr.length - 1 ? "1px" : "0",
                      }}
                    >
                      {line}
                    </div>
                  ))
                : details}
            </div>
          </>
        ) : (
          <div className='w-full max-h-[45vh] overflow-y-auto px-1 text-lg text-center break-words text-dark-blue [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-thumb]:bg-[#d7dbe3]'>
            {typeof message === "string"
              ? message.split(/\n|<br\s*\/?>/).map((line, idx, arr) => (
                  <div
                    key={idx}
                    style={{
                      marginBottom: idx !== arr.length - 1 ? "1px" : "0",
                    }}
                  >
                    {line}
                  </div>
                ))
              : message}
          </div>
        )}
        <div>
          <button
            type='button'
            onClick={handleClose}
            className='px-6 py-3 mt-5 mr-3 font-medium text-white rounded-full bg-primary-blue transition-transform hover:scale-105 hover:bg-second-blue'
          >
            {buttonLogout ? "Login" : errorList ? "Cancel" : "Close"}
          </button>
          {errorList && (
            <button
              onClick={handleAccept}
              className='px-6 py-3 mt-5 mr-3 font-medium text-white rounded-full bg-primary-blue transition-transform hover:scale-105 hover:bg-second-blue'
            >
              Accept
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalError;
